import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Sparkles } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import UrgencyBanner from "../components/UrgencyBanner";
import JournalNewsletter from "../components/JournalNewsletter";
import useSeo from "../lib/useSeo";
import { PUBLISHED_POSTS, getCategoryColor } from "../data/journal";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

export default function Journal() {
  useSeo({
    title: "The Plate Buddies Journal — family dinner ideas, meal planning, and screen-free rituals",
    description:
      "Real, tested advice from real parents. Family dinner ideas, weekly meal-planning methods, and screen-free rituals that actually work.",
    url: "https://platebuddies.com/journal",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [featured, ...rest] = PUBLISHED_POSTS;

  return (
    <div className="font-body min-h-screen bg-pb-cream">
      <UrgencyBanner />
      <Navbar />

      <header className="relative pt-16 pb-10 md:pt-24 md:pb-14 overflow-hidden">
        <div className="blob bg-pb-sun" style={{ width: 380, height: 380, top: -120, right: -80, opacity: 0.4 }} />
        <div className="max-w-5xl mx-auto px-5 sm:px-8 relative">
          <p className="text-sm font-extrabold uppercase tracking-widest text-pb-coral inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> The Plate Buddies Journal
          </p>
          <h1 className="font-display font-black text-4xl md:text-6xl mt-3 leading-[1.05] text-pb-ink max-w-4xl">
            Real dinner help for real families.
          </h1>
          <p className="mt-4 text-pb-soft text-lg max-w-2xl leading-relaxed">
            Family dinner playbooks, 60-second meal plans, and screen-free rituals — written by parents who've been at the 4:47 PM fridge stare too.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 pb-20">
        {/* Featured post */}
        {featured && (
          <Link
            to={`/journal/${featured.slug}`}
            data-testid={`journal-featured-${featured.slug}`}
            className="block group rounded-[2.5rem] overflow-hidden bg-white shadow-[0_20px_50px_rgba(28,22,40,0.08)] hover:shadow-[0_30px_70px_rgba(28,22,40,0.15)] transition-shadow"
          >
            <div className="grid md:grid-cols-2 gap-0">
              <div className="relative h-64 md:h-auto md:min-h-[360px] overflow-hidden">
                <img
                  src={featured.hero_image}
                  alt={featured.hero_alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <span
                  className="absolute top-5 left-5 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest text-white shadow"
                  style={{ background: getCategoryColor(featured.category) }}
                >
                  {featured.category}
                </span>
              </div>
              <div className="p-7 md:p-10 flex flex-col justify-center">
                <p className="text-[11px] font-extrabold uppercase tracking-widest text-pb-coral">
                  New this week
                </p>
                <h2 className="font-display font-black text-3xl md:text-4xl mt-3 leading-[1.1] text-pb-ink group-hover:text-pb-coral transition-colors">
                  {featured.title}
                </h2>
                <p className="mt-3 text-pb-soft leading-relaxed">{featured.excerpt}</p>
                <div className="mt-5 flex items-center gap-4 text-xs text-pb-soft">
                  <span className="inline-flex items-center gap-1 font-bold">
                    <Clock className="h-3 w-3" /> {featured.read_time_min} min read
                  </span>
                  <span>{formatDate(featured.published_at)}</span>
                </div>
                <p className="mt-6 text-sm font-black text-pb-ink inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                  Read the story <ArrowRight className="h-4 w-4" />
                </p>
              </div>
            </div>
          </Link>
        )}

        {/* Rest of the posts */}
        {rest.length > 0 && (
          <div className="mt-14 grid md:grid-cols-2 gap-6 md:gap-8">
            {rest.map((post) => (
              <Link
                key={post.slug}
                to={`/journal/${post.slug}`}
                data-testid={`journal-card-${post.slug}`}
                className="group bg-white rounded-[2rem] overflow-hidden shadow-[0_14px_36px_rgba(28,22,40,0.07)] hover:shadow-[0_22px_50px_rgba(28,22,40,0.12)] hover:-translate-y-1 transition-all duration-300"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={post.hero_image}
                    alt={post.hero_alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <span
                    className="absolute top-4 left-4 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-white shadow"
                    style={{ background: getCategoryColor(post.category) }}
                  >
                    {post.category}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-display font-black text-xl leading-tight text-pb-ink group-hover:text-pb-coral transition-colors">
                    {post.title}
                  </h3>
                  <p className="mt-2 text-sm text-pb-soft line-clamp-2 leading-snug">
                    {post.excerpt}
                  </p>
                  <div className="mt-4 flex items-center gap-3 text-[11px] text-pb-soft">
                    <span className="inline-flex items-center gap-1 font-bold">
                      <Clock className="h-3 w-3" /> {post.read_time_min} min
                    </span>
                    <span>·</span>
                    <span>{formatDate(post.published_at)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Newsletter capstone — retention play */}
        <div className="mt-16">
          <JournalNewsletter variant="card" />
        </div>

        {/* Product CTA capstone — activation play */}
        <div className="mt-8 rounded-[2.5rem] bg-gradient-to-br from-pb-coral to-[#FF9B7A] p-8 md:p-12 text-white text-center">
          <p className="text-xs font-extrabold uppercase tracking-widest opacity-90">
            Try the tools we write about
          </p>
          <h3 className="font-display font-black text-3xl md:text-4xl mt-2 leading-tight">
            Design your kid&rsquo;s own food character — free.
          </h3>
          <p className="mt-3 opacity-90 max-w-xl mx-auto">
            The character-play trick from these posts, in a 30-second generator.
          </p>
          <Link
            to="/free-buddy"
            data-testid="btn-journal-cta"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-pb-coral font-black px-7 py-4 shadow-xl hover:scale-105 transition"
          >
            Make my Buddy <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
